import {
  resolveTicketMessageHtml,
  resolveTicketMessageHtmlForEmail,
} from './tickets';
import { formatSingaporeDateTimeWithSeconds } from '../utils/dateTime';
import type { TicketAttachment, TicketMessage } from '../types/tickets';

/** Messages that may appear in customer-facing reply/forward bodies. */
export function customerVisibleThreadMessages(messages: TicketMessage[]): TicketMessage[] {
  return messages.filter((msg) => msg.direction !== 'note');
}

/** Ticket UI / email quote timestamps: dd/mm/yyyy, hh:mm:ss (24h, Singapore). */
export function formatTicketDateTime(iso: string): string {
  return formatSingaporeDateTimeWithSeconds(iso);
}

function uniqueEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of emails) {
    const email = raw.trim().toLowerCase();
    if (!email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

function normEmail(value: string | undefined | null): string {
  return String(value ?? '').trim().toLowerCase();
}

/** Support mailbox + signed-in agent — never put these in Reply To/Cc. */
export function collectExcludedReplyEmails(
  messages: TicketMessage[],
  mailboxEmail?: string,
  agentEmail?: string,
): Set<string> {
  const exclude = new Set<string>();
  const mailbox = normEmail(mailboxEmail);
  const agent = normEmail(agentEmail);
  if (mailbox.includes('@')) exclude.add(mailbox);
  if (agent.includes('@')) exclude.add(agent);
  for (const msg of messages) {
    if (msg.direction === 'outbound' && normEmail(msg.from_email).includes('@')) {
      exclude.add(normEmail(msg.from_email));
    }
  }
  return exclude;
}

/**
 * Outlook/helpdesk-style recipients:
 * - To = latest customer (last inbound From), never the support mailbox
 * - Reply keeps that message's Cc; Reply all adds other thread participants
 */
export function resolveReplyRecipients(
  messages: TicketMessage[],
  options: {
    mailboxEmail?: string;
    agentEmail?: string;
    fallbackTo?: string;
    replyAll?: boolean;
  },
): { to: string[]; cc: string[] } {
  const mailbox = normEmail(options.mailboxEmail);
  const agent = normEmail(options.agentEmail);
  const ourAddrs = new Set<string>();
  if (mailbox.includes('@')) ourAddrs.add(mailbox);
  for (const msg of messages) {
    if (msg.direction === 'outbound' && normEmail(msg.from_email).includes('@')) {
      ourAddrs.add(normEmail(msg.from_email));
    }
  }

  const sorted = [...customerVisibleThreadMessages(messages)].sort(
    (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
  );
  const lastInbound = sorted.find((m) => m.direction === 'inbound');
  const latest = sorted[0];

  let to: string[] = [];

  // Prefer the customer who last wrote in.
  if (lastInbound && normEmail(lastInbound.from_email).includes('@')) {
    const from = normEmail(lastInbound.from_email);
    if (!ourAddrs.has(from)) to = [from];
  }

  // Continuing after our own outbound: use who we wrote to (not ourselves / mailbox).
  if (to.length === 0 && latest?.direction === 'outbound') {
    to = uniqueEmails(latest.to_emails).filter((e) => !ourAddrs.has(e) && e !== agent);
  }

  if (to.length === 0) {
    const fb = normEmail(options.fallbackTo);
    if (fb.includes('@') && !ourAddrs.has(fb)) to = [fb];
  }

  // Last resort for self-mail tests: still prefill requester / inbound from.
  if (to.length === 0 && lastInbound && normEmail(lastInbound.from_email).includes('@')) {
    const from = normEmail(lastInbound.from_email);
    if (from !== mailbox) to = [from];
  }
  if (to.length === 0) {
    const fb = normEmail(options.fallbackTo);
    if (fb.includes('@') && fb !== mailbox) to = [fb];
  }

  const toSet = new Set(to);
  const excludeCc = new Set<string>([...ourAddrs, ...toSet]);
  if (agent.includes('@')) excludeCc.add(agent);

  let cc: string[] = [];

  if (options.replyAll) {
    const pool: string[] = [];
    for (const msg of customerVisibleThreadMessages(messages)) {
      pool.push(msg.from_email, ...msg.to_emails, ...msg.cc_emails);
    }
    cc = uniqueEmails(pool).filter((e) => !excludeCc.has(e));
  } else {
    // Reply: keep Cc from the customer message (and latest outbound if continuing).
    const pool: string[] = [...(lastInbound?.cc_emails ?? [])];
    if (latest?.direction === 'outbound') {
      pool.push(...latest.cc_emails);
    }
    cc = uniqueEmails(pool).filter((e) => !excludeCc.has(e));
  }

  return { to: to.slice(0, 1), cc };
}

/** @deprecated Prefer resolveReplyRecipients */
export function collectReplyAllEmails(
  messages: TicketMessage[],
  mailboxEmail: string,
  defaultTo: string,
): { to: string[]; cc: string[] } {
  return resolveReplyRecipients(messages, {
    mailboxEmail,
    fallbackTo: defaultTo,
    replyAll: true,
  });
}

/**
 * Split a message body into the author's new content vs quoted history
 * (Outlook / Gmail / our compose quote blocks).
 */
export function splitQuotedEmailHtml(html: string): { main: string; quoted: string } {
  const raw = String(html ?? '');
  if (!raw.trim()) return { main: '', quoted: '' };

  const markers: number[] = [];
  const pushMatch = (re: RegExp) => {
    const m = raw.search(re);
    if (m >= 0) markers.push(m);
  };

  pushMatch(/<div[^>]*style="[^"]*border-top:\s*1px solid #e5e7eb/i);
  pushMatch(/<div[^>]*class="[^"]*gmail_quote/i);
  pushMatch(/<blockquote[^>]*class="[^"]*gmail_quote/i);
  pushMatch(/<div[^>]*id=["']?appendonsend/i);
  pushMatch(/<div[^>]*id=["']?divRplyFwdMsg/i);
  pushMatch(/<hr[^>]*id=["']?replySplit/i);
  pushMatch(/(?:^|>)(?:\s|&nbsp;|<br\s*\/?>)*-{5,}\s*Original Message\s*-{5,}/i);
  pushMatch(/(?:^|>)(?:\s|&nbsp;|<br\s*\/?>)*From:\s*[\s\S]{0,120}?Sent:\s*/i);
  pushMatch(/<b>From:<\/b>[\s\S]{0,80}?<b>Sent:<\/b>/i);
  pushMatch(/On .+ wrote:\s*(?:<br\s*\/?>)?/i);

  const cutCandidates = markers.filter((i) => i >= 24);
  if (cutCandidates.length === 0) return { main: raw, quoted: '' };

  const cut = Math.min(...cutCandidates);
  const main = raw.slice(0, cut).trim();
  const quoted = raw.slice(cut).trim();
  if (!main || !quoted) return { main: raw, quoted: '' };
  return { main, quoted };
}

/** Only the author-written part — strips nested reply history so quotes don't multiply. */
export function messageMainHtml(html: string): string {
  const { main } = splitQuotedEmailHtml(html);
  return (main || html || '').trim();
}

/**
 * Build reply/forward history once per message (newest first).
 * When stripNestedQuotes is true, each body drops previously-quoted thread content
 * so outbound quotes don't multiply. For on-screen preview, pass false to keep full bodies.
 */
export function buildQuotedThreadHtml(
  messages: TicketMessage[],
  resolvedBodies?: Record<string, string>,
  stripNestedQuotes = true,
): string {
  const visible = customerVisibleThreadMessages(messages);
  if (visible.length === 0) return '';
  const blocks = [...visible]
    .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime())
    .map((msg) => {
      const from = msg.from_name
        ? `${msg.from_name} &lt;${msg.from_email}&gt;`
        : msg.from_email || 'Unknown';
      const to = msg.to_emails.join('; ') || '—';
      const cc = msg.cc_emails.length ? `<br>Cc: ${msg.cc_emails.join('; ')}` : '';
      const rawBody =
        resolvedBodies?.[msg.id] ??
        (msg.body_html || msg.body_text.replace(/\n/g, '<br>'));
      const body = stripNestedQuotes ? messageMainHtml(rawBody) : (rawBody || '').trim();
      if (!body) return '';
      return `<div style="border-top:1px solid #e5e7eb;margin:16px 0 0;padding:12px 0 0;max-width:100%;overflow-wrap:anywhere;word-break:break-word;">
<div style="font-size:12px;color:#6b7280;margin-bottom:8px;">
<b>From:</b> ${from}<br>
<b>Sent:</b> ${formatTicketDateTime(msg.sent_at)}<br>
<b>To:</b> ${to}${cc}<br>
<b>Subject:</b> ${msg.subject || '—'}
</div>
<div style="font-size:11pt;color:#374151;line-height:1.15;max-width:100%;overflow-wrap:anywhere;word-break:break-word;">${body}</div>
</div>`;
    })
    .filter(Boolean);
  return blocks.join('');
}

export function wrapComposeHtml(userHtml: string, quotedHtml: string): string {
  const user = userHtml.trim();
  if (!quotedHtml.trim()) return user;
  const top = user || '<p></p>';
  return `${top}<br><br>${quotedHtml}`;
}

/** Resolve inline images in each message before building the quoted thread block. */
export async function buildResolvedQuotedThreadHtml(
  messages: TicketMessage[],
  attachmentsByMessage: Record<string, TicketAttachment[]>,
  forEmail: boolean,
  stripNestedQuotes = true,
): Promise<string> {
  const visible = customerVisibleThreadMessages(messages);
  if (visible.length === 0) return '';
  const resolve = forEmail ? resolveTicketMessageHtmlForEmail : resolveTicketMessageHtml;
  const resolvedBodies: Record<string, string> = {};

  await Promise.all(
    visible.map(async (msg) => {
      const raw = msg.body_html || msg.body_text.replace(/\n/g, '<br>');
      const atts = attachmentsByMessage[msg.id] ?? [];
      const full =
        atts.length > 0 &&
        (raw.includes('cid:') || raw.includes('ticket-inline://') || /<img\b/i.test(raw))
          ? await resolve(raw, atts)
          : raw;
      resolvedBodies[msg.id] = stripNestedQuotes ? messageMainHtml(full) : full;
    }),
  );

  return buildQuotedThreadHtml(visible, resolvedBodies, stripNestedQuotes);
}
